import { FormHelperText, makeStyles, Typography } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import clsx from 'clsx';

interface Props {
  multiple?: boolean;
  maxSize?: number;
  title?: string;
  activeTitle?: string;
  onImageSelected: (file: any) => void;
  disablePasteFunc?: boolean;
  disabled?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: 170,
    backgroundColor: theme.palette.grey[50],
    border: '0.5px dashed grey',
    boxShadow: theme.shadows[3],
    borderRadius: 8,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    '&.disabled': {
      backgroundColor: theme.palette.grey[200],
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  },
  rejectedFilesDiv: {
    padding: theme.spacing(1.5)
  }
}));

const component = ({
  multiple = true,
  maxSize = 5300000,
  title = 'Drag and drop some image(s) here, or click to select image(s)',
  activeTitle = 'Release Mouse',
  onImageSelected,
  disablePasteFunc,
  disabled = false
}: Props) => {
  const classes = useStyles();

  const onDrop = useCallback(
    (acceptedFiles) => {
      onImageSelected(acceptedFiles);
      // Do something with the files
    },
    [onImageSelected]
  );

  const {
    inputRef,
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections
  } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png', '.jpeg', '.jpg', '.webp']
    },
    maxSize,
    multiple
  });

  useEffect(() => {
    const handlePasteFile = async (event: ClipboardEvent) => {
      if (event.clipboardData?.files) {
        const formikInputRef: any = inputRef;
        formikInputRef.current.files = event.clipboardData.files;
        formikInputRef.current?.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      }
    };

    if (!disablePasteFunc) {
      document.addEventListener('paste', handlePasteFile);
    } else {
      document.removeEventListener('paste', handlePasteFile);
    }

    return () => {
      document.removeEventListener('paste', handlePasteFile);
    };
  }, [disablePasteFunc, inputRef]);

  const fileRejectionItems = fileRejections?.map(({ file, errors }) => {
    return (
      <li key={file?.name}>
        {file?.name} - {file?.size} bytes
        <ul>
          {errors.map((e) => (
            <li key={e?.code}>{e?.message}</li>
          ))}
        </ul>
      </li>
    );
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={clsx(classes.root, disabled && 'disabled')}
      >
        <input {...getInputProps()} />
        <Typography variant="h5">
          <input {...getInputProps()} />
          {isDragActive ? activeTitle : title}
        </Typography>
        <AddPhotoAlternateIcon />
      </div>
      <FormHelperText>
        Size Up to 5MB and only jpeg, jpg and png is allowed
      </FormHelperText>

      {fileRejections?.length > 0 ? (
        <div className={classes.rejectedFilesDiv}>
          <h4>Rejected file(s)</h4>
          <ul>{fileRejectionItems}</ul>
        </div>
      ) : null}
    </div>
  );
};

export const DragAndDropImgUpload = React.memo(component);
